import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as DismissIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  DoneAll as ReviewAllIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as articleService from '../services/articleService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import AssigneeCell from '../components/common/AssigneeCell';

const ArticleReportManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [reports, setReports] = useState<articleService.ArticleReportSummary[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailArticleId, setDetailArticleId] = useState<string | null>(null);
  const [detailArticleTitle, setDetailArticleTitle] = useState('');
  const [detailReports, setDetailReports] = useState<articleService.ArticleReportDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Action dialog
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionTarget, setActionTarget] = useState<{
    type: 'single' | 'bulk';
    reportId?: string;
    articleId?: string;
    articleTitle?: string;
  } | null>(null);
  const [actionType, setActionType] = useState<'dismiss' | 'archive' | 'delete'>('dismiss');
  const [adminNote, setAdminNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load reported articles
  const loadReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { status?: string; reason?: string } = {};
      if (statusFilter) filters.status = statusFilter;
      if (reasonFilter) filters.reason = reasonFilter;

      const response = await articleService.getReportedArticles(page + 1, rowsPerPage, filters);
      setReports(response.docs || []);
      setTotalDocs(response.totalDocs || 0);
    } catch (err: any) {
      setError(err.message || 'خطا در بارگذاری گزارش‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, reasonFilter]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Load detail reports for an article
  const handleViewReports = async (articleId: string, articleTitle: string) => {
    setDetailArticleId(articleId);
    setDetailArticleTitle(articleTitle);
    setDetailDialogOpen(true);
    setDetailLoading(true);
    try {
      const response = await articleService.getArticleReports(articleId, 1, 50);
      setDetailReports(response.docs || []);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در بارگذاری جزئیات', { variant: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  // Open action dialog
  const handleActionClick = (
    type: 'single' | 'bulk',
    action: 'dismiss' | 'archive' | 'delete',
    reportId?: string,
    articleId?: string,
    articleTitle?: string
  ) => {
    setActionTarget({ type, reportId, articleId, articleTitle });
    setActionType(action);
    setAdminNote('');
    setActionDialogOpen(true);
  };

  // Execute action
  const handleActionConfirm = async () => {
    if (!actionTarget) return;
    setSubmitting(true);
    try {
      if (actionTarget.type === 'single' && actionTarget.reportId) {
        await articleService.reviewReport(actionTarget.reportId, actionType, adminNote || undefined);
        enqueueSnackbar('گزارش بررسی شد', { variant: 'success' });
      } else if (actionTarget.type === 'bulk' && actionTarget.articleId) {
        const result = await articleService.reviewAllArticleReports(
          actionTarget.articleId,
          actionType,
          adminNote || undefined
        );
        enqueueSnackbar(`${result.modifiedCount || 0} گزارش بررسی شد`, { variant: 'success' });
      }
      setActionDialogOpen(false);
      setActionTarget(null);
      setAdminNote('');

      // Refresh
      if (detailDialogOpen && detailArticleId) {
        handleViewReports(detailArticleId, detailArticleTitle);
      }
      loadReports();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در بررسی گزارش', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Format helpers
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  const getUniqueReasons = (reasons: string[]) => {
    const unique = Array.from(new Set(reasons));
    return unique.map((r) => articleService.getReportReasonLabel(r));
  };

  const getActionLabel = (action: 'dismiss' | 'archive' | 'delete') => {
    const labels: Record<string, string> = {
      dismiss: 'رد گزارش',
      archive: 'آرشیو مقاله',
      delete: 'حذف مقاله',
    };
    return labels[action];
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <MyShiftStatusBanner category="article_report" />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <FlagIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          گزارش‌های مقالات
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadReports} disabled={loading}>
          بروزرسانی
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>وضعیت گزارش</InputLabel>
            <Select
              value={statusFilter}
              label="وضعیت گزارش"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="pending">در انتظار بررسی</MenuItem>
              <MenuItem value="reviewed">بررسی شده</MenuItem>
              <MenuItem value="action_taken">اقدام شده</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>دلیل گزارش</InputLabel>
            <Select
              value={reasonFilter}
              label="دلیل گزارش"
              onChange={(e) => {
                setReasonFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="inappropriate_content">محتوای نامناسب</MenuItem>
              <MenuItem value="spam">اسپم</MenuItem>
              <MenuItem value="copyright">نقض کپی‌رایت</MenuItem>
              <MenuItem value="misinformation">اطلاعات نادرست</MenuItem>
              <MenuItem value="harassment">آزار و اذیت</MenuItem>
              <MenuItem value="other">سایر</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>عنوان مقاله</TableCell>
                <TableCell>نویسنده</TableCell>
                <TableCell align="center">تعداد کل گزارش</TableCell>
                <TableCell align="center">در انتظار بررسی</TableCell>
                <TableCell>دلایل</TableCell>
                <TableCell>آخرین گزارش</TableCell>
                <TableCell>وضعیت مقاله</TableCell>
                <TableCell>مسئول</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      گزارشی وجود ندارد
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report.articleId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {report.articleTitle || 'نامشخص'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {report.authorName || 'نامشخص'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<FlagIcon />}
                        label={report.totalReports}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {report.pendingReports > 0 ? (
                        <Chip
                          label={report.pendingReports}
                          color="warning"
                          size="small"
                        />
                      ) : (
                        <Chip label="0" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {getUniqueReasons(report.reasons).map((reason, idx) => (
                          <Chip key={idx} label={reason} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {report.latestReport ? formatDate(report.latestReport) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={articleService.getStatusLabel(report.articleStatus)}
                        color={articleService.getStatusColor(report.articleStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {/* A row is one ARTICLE but assignment is per-report, so the
                          backend sends `assignedTo` only when the whole group has a
                          single owner; otherwise show how many admins are involved. */}
                      {((report as any).assignees?.length ?? 0) > 1 ? (
                        <span className="tag tag-sm tag-info">
                          {(report as any).assignees.length} مسئول
                        </span>
                      ) : (
                        <AssigneeCell value={(report as any).assignedTo} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                        <Tooltip title="مشاهده گزارش‌ها">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleViewReports(report.articleId, report.articleTitle)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {report.pendingReports > 0 && (
                          <>
                            <Tooltip title="رد همه گزارش‌ها">
                              <IconButton
                                color="default"
                                size="small"
                                onClick={() =>
                                  handleActionClick('bulk', 'dismiss', undefined, report.articleId, report.articleTitle)
                                }
                              >
                                <DismissIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="آرشیو مقاله">
                              <IconButton
                                color="warning"
                                size="small"
                                onClick={() =>
                                  handleActionClick('bulk', 'archive', undefined, report.articleId, report.articleTitle)
                                }
                              >
                                <ArchiveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="حذف مقاله">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleActionClick('bulk', 'delete', undefined, report.articleId, report.articleTitle)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalDocs}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} از ${count}`}
        />
      </Paper>

      {/* ==================== DETAIL DIALOG ==================== */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setDetailArticleId(null);
          setDetailReports([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          گزارش‌های مقاله: {detailArticleTitle}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailReports.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              گزارشی وجود ندارد
            </Typography>
          ) : (
            <List>
              {detailReports.map((report, idx) => (
                <React.Fragment key={report._id}>
                  {idx > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      report.status === 'pending' ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="رد گزارش">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleActionClick('single', 'dismiss', report._id, detailArticleId || undefined, detailArticleTitle)
                              }
                            >
                              <DismissIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Chip
                          label={articleService.getReportStatusLabel(report.status)}
                          color={articleService.getReportStatusColor(report.status)}
                          size="small"
                        />
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={report.reportedBy?.profileImage}>
                        {report.reportedBy?.name?.charAt(0) || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {report.reportedBy?.name || 'نامشخص'}
                          </Typography>
                          <Chip
                            label={articleService.getReportReasonLabel(report.reason)}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(report.createdAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          {report.description && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {report.description}
                            </Typography>
                          )}
                          {report.adminNote && (
                            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                              یادداشت ادمین: {report.adminNote}
                            </Typography>
                          )}
                          {report.actionTaken && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              اقدام: {report.actionTaken === 'dismissed' ? 'رد شده' : report.actionTaken === 'archived' ? 'آرشیو شده' : 'حذف شده'}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          {detailArticleId && detailReports.some((r) => r.status === 'pending') && (
            <>
              <Button
                variant="outlined"
                startIcon={<ReviewAllIcon />}
                onClick={() =>
                  handleActionClick('bulk', 'dismiss', undefined, detailArticleId, detailArticleTitle)
                }
              >
                رد همه
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<ArchiveIcon />}
                onClick={() =>
                  handleActionClick('bulk', 'archive', undefined, detailArticleId, detailArticleTitle)
                }
              >
                آرشیو مقاله
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() =>
                  handleActionClick('bulk', 'delete', undefined, detailArticleId, detailArticleTitle)
                }
              >
                حذف مقاله
              </Button>
            </>
          )}
          <Button
            onClick={() => {
              setDetailDialogOpen(false);
              setDetailArticleId(null);
              setDetailReports([]);
            }}
          >
            بستن
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== ACTION CONFIRMATION DIALOG ==================== */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => {
          setActionDialogOpen(false);
          setActionTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {getActionLabel(actionType)}
          {actionTarget?.articleTitle ? ` - ${actionTarget.articleTitle}` : ''}
        </DialogTitle>
        <DialogContent>
          {actionType === 'dismiss' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {actionTarget?.type === 'bulk'
                ? 'تمام گزارش‌های در انتظار بررسی این مقاله رد خواهند شد.'
                : 'این گزارش رد و به عنوان بررسی‌شده علامت‌گذاری می‌شود.'}
            </Alert>
          )}
          {actionType === 'archive' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              مقاله آرشیو شده و تمام گزارش‌های در انتظار بررسی شده علامت‌گذاری می‌شوند.
            </Alert>
          )}
          {actionType === 'delete' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              مقاله حذف شده و تمام گزارش‌های در انتظار بررسی شده علامت‌گذاری می‌شوند.
              این عملیات قابل بازگشت نیست.
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="یادداشت ادمین (اختیاری)"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActionDialogOpen(false);
              setActionTarget(null);
            }}
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            color={actionType === 'delete' ? 'error' : actionType === 'archive' ? 'warning' : 'primary'}
            onClick={handleActionConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : undefined}
          >
            {getActionLabel(actionType)}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ArticleReportManagement;
